import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, moneyToInput, parseInputToApi } from '@/lib/money';
import type { LoanDetail } from './types';

/**
 * Ajustar o valor de uma parcela — RF-DIV-11.
 *
 * ================================================================
 * DIGITA O VALOR, APLICA
 * ================================================================
 *
 * "Só queria poder ajustar o valor da dívida, pois ela vai caindo ao longo do
 * mês e eu vou ajustar isso manualmente."
 *
 * A primeira versão desta tela resolvia a taxa implícita e reescrevia todas as
 * parcelas restantes com o mesmo valor — o que exige que elas somem o saldo
 * inteiro, e por isso RECUSAVA valor menor. Fazia o contrário do que foi pedido.
 *
 * Agora ajusta UMA parcela, e a diferença vai para a última, para a dívida
 * continuar fechando. A tela mostra a nova última antes de aplicar: pagar menos
 * agora é pagar mais depois, e isso precisa estar visível.
 */

type Preview =
  | { possible: false; reason: string }
  | {
      possible: true;
      installment: number;
      current_amount: string;
      new_amount: string;
      difference: string;
      last_installment: number;
      last_amount_before: string;
      last_amount_after: string;
    };

export function AdjustInstallmentDialog({
  loan,
  selectedIds,
  onClose,
  onSaved,
}: {
  loan: LoanDetail;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  /**
   * A última fica FORA da lista: é ela que absorve a diferença das outras.
   * Oferecê-la seria oferecer uma opção que a API recusa.
   */
  const ultimaNumero = loan.installments[loan.installments.length - 1]?.number;
  const ajustaveis = loan.installments.filter((p) => !p.paid_at && p.number !== ultimaNumero);
  const primeira = ajustaveis[0];

  const [numero, setNumero] = useState(String(primeira?.number ?? 1));
  const [valor, setValor] = useState(moneyToInput(primeira?.total ?? '0'));
  const [motivo, setMotivo] = useState('');

  const amount = parseInputToApi(valor);

  const simulacao = useQuery({
    queryKey: ['ajuste-parcela', loan.id, amount, numero],
    queryFn: () =>
      api.get<{ data: Preview }>(
        `/debts/${loan.id}/ajuste-parcela/simular?amount=${amount}&from_installment=${numero}`,
        { entityIds: selectedIds },
      ),
    enabled: amount !== '' && Number(amount) >= 0,
    select: (r) => r.data,
  });

  const salvar = useMutation({
    mutationFn: () =>
      api.post(
        `/debts/${loan.id}/ajuste-parcela`,
        { amount, from_installment: Number(numero), reason: motivo || undefined },
        { entityIds: selectedIds },
      ),
    onSuccess: onSaved,
  });

  const erro = salvar.error instanceof ApiError ? salvar.error : null;
  const sim = simulacao.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">Ajustar valor da parcela</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            salvar.mutate();
          }}
          className="space-y-4"
        >
          {erro && (
            <div
              role="alert"
              className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
            >
              {erro.message}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Parcela
              </span>
              <select
                value={numero}
                onChange={(e) => {
                  setNumero(e.target.value);
                  const p = ajustaveis.find((x) => String(x.number) === e.target.value);
                  if (p) setValor(moneyToInput(p.total));
                }}
                className="input w-full"
              >
                {ajustaveis.map((p) => (
                  <option key={p.id} value={p.number}>
                    {p.number} de {loan.installments.length} · {formatMoney(p.total)}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Novo valor
              </span>
              <input
                value={valor}
                onChange={(e) => setValor(maskMoneyInput(e.target.value))}
                inputMode="numeric"
                autoFocus
                className="input w-full"
              />
            </label>
          </div>

          {sim && !sim.possible && (
            <div
              role="alert"
              className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
            >
              {sim.reason}
            </div>
          )}

          {/* A consequência, dita antes de aplicar: pagar menos agora é pagar
              mais depois, e o número precisa estar na frente de quem decide. */}
          {sim?.possible && sim.difference !== '0.0000' && (
            <p className="rounded-lg bg-slate-50 px-3.5 py-3 text-sm dark:bg-slate-800/50">
              A diferença de <strong>{formatMoney(sim.difference)}</strong> vai para a{' '}
              <strong>parcela {sim.last_installment}</strong>, que passa de{' '}
              {formatMoney(sim.last_amount_before)} para{' '}
              <strong>{formatMoney(sim.last_amount_after)}</strong>.
            </p>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Motivo <span className="font-normal text-slate-400">(opcional)</span>
            </span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={500}
              placeholder="Ex.: valor que veio no boleto de setembro"
              className="input w-full text-sm"
            />
          </label>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvar.isPending || sim?.possible === false}
              className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {salvar.isPending ? 'Aplicando…' : 'Aplicar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
